import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useUserData } from '../hooks/useUserData';
import { useContracts } from '../hooks/useContract';
import { formatTimestamp } from '../utils/format';
import { shortenAddress } from '../utils/wallet';
import { getPackageColor } from '../config/packageColors';
import ConnectButton from '../components/common/ConnectButton';
import CopyButton from '../components/common/CopyButton';
import EarningsOverview from '../components/dashboard/EarningsOverview';
import AvailableClaim from '../components/dashboard/AvailableClaim';
import SponsorActions from '../components/dashboard/SponsorActions';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import NextPackageCard from '../components/dashboard/NextPackageCard';
import ActivateForModal from '../components/dashboard/ActivateForModal';

const Home = () => {
  const { connected } = useWallet();
  const navigate = useNavigate();
  const { userId, user, income, heldBucket, heldTarget, isRegistered, refresh } = useUserData();
  const { storage } = useContracts();

  const [activateForWallet, setActivateForWallet] = useState(null); // null = closed; string (may be '') = open with prefill
  const [uplineWallet, setUplineWallet] = useState(null);

  // Look up sponsor's wallet for the hero card
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const rid = user?.referrerId;
      if (!rid) { setUplineWallet(null); return; }
      try {
        const u = await storage.users(rid);
        if (!cancelled) setUplineWallet(u[0] || null);
      } catch (_) {
        if (!cancelled) setUplineWallet(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.referrerId, storage]);

  // ============ DASHBOARD MODE ============
  if (connected && isRegistered && user) {
    const baseUrl = typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}`
      : '';
    const refLink = `${baseUrl}/register?ref=${userId}`;
    const topPkg = user.topPackageId || 0;
    const pkgColor = topPkg > 0 ? getPackageColor(topPkg) : null;

    return (
      <div className="main-content">
        {/* 1. USER HERO — User ID + colored package badge, joined date + upline below */}
        <div className="hero-user-card">
          <div className="hero-user-orb hero-user-orb-1"></div>
          <div className="hero-user-orb hero-user-orb-2"></div>
          <div className="hero-user-content">
            <div className="hero-user-avatar">
              <span>{String(userId).charAt(0)}</span>
            </div>
            <div className="hero-user-info">
              <div className="hero-user-greeting">Welcome back 👋</div>

              <div className="hero-user-idrow">
                <div className="hero-user-idlabel">
                  <span className="hui-label">USER ID :</span>
                  <span className="hui-value">{userId}</span>
                </div>
                {topPkg > 0 && pkgColor && (
                  <span
                    className="hero-user-pkgbadge"
                    style={{
                      background: `linear-gradient(135deg, ${pkgColor.from}, ${pkgColor.to})`,
                      boxShadow: `0 6px 20px -4px ${pkgColor.glow}`
                    }}
                  >
                    ⚡ P{topPkg}
                  </span>
                )}
                {topPkg === 0 && (
                  <span className="hero-user-pkgbadge hero-user-pkgbadge-none">No Package</span>
                )}
              </div>

              <div className="hero-user-facts">
                <div className="huf-item">
                  <span className="huf-label">Joined Date:</span>
                  <span className="huf-value">{formatTimestamp(user.joinTime).split(',')[0]}</span>
                </div>
                <div className="huf-item">
                  <span className="huf-label">Upline:</span>
                  <span className="huf-value font-mono">
                    {uplineWallet ? shortenAddress(uplineWallet, 4) : '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. EARNINGS HERO (no ceiling) */}
        <EarningsOverview user={user} income={income} userId={userId} />

        {/* 2b. NEXT PACKAGE ACTIVATION */}
        <div style={{ marginTop: 16 }}>
          <NextPackageCard
            topPackageId={user.topPackageId}
            heldBucket={heldBucket}
            onActivated={refresh}
          />
        </div>

        {/* 3. REFERRAL LINK */}
        <div className="section-heading">
          <span className="section-heading-bar"></span>
          Your Referral Link
        </div>
        <div className="ref-card">
          <div className="ref-card-inside">
            <div className="ref-id-pill">
              <div className="ref-id-label">Your ID</div>
              <div className="ref-id-value">{userId}</div>
            </div>
            <div className="ref-link-content">
              <div className="ref-link-label">Share to invite</div>
              <div className="ref-link-value">{refLink}</div>
            </div>
            <CopyButton text={refLink} label="Copy" copiedLabel="Copied!" />
          </div>
        </div>

        {/* 4. AVAILABLE BALANCE (royalty pending + held bucket) */}
        <div className="section-heading">
          <span className="section-heading-bar"></span>
          Your Available Balance
        </div>
        <AvailableClaim
          userId={userId}
          heldBucket={heldBucket}
          heldTarget={heldTarget}
          topPackageId={user.topPackageId}
          onClaimed={refresh}
        />

        {/* 7. SPONSOR ACTIONS */}
        <div className="section-heading">
          <span className="section-heading-bar"></span>
          Quick Actions
        </div>
        <SponsorActions
          userId={userId}
          onSuccess={refresh}
          onActivateClick={(prefill) => setActivateForWallet(prefill || '')}
        />

        {/* 9. RECENT ACTIVITY */}
        <div className="section-heading">
          <span className="section-heading-bar"></span>
          Recent Activity
        </div>
        <ActivityFeed userId={userId} limit={15} />

        {/* Shared "activate for user" modal — opens from Quick Actions or from inactive list */}
        {activateForWallet !== null && (
          <ActivateForModal
            initialWallet={activateForWallet}
            onClose={() => setActivateForWallet(null)}
            onSuccess={refresh}
          />
        )}
      </div>
    );
  }

  // ============ LANDING ============
  return (
    <div className="main-content">
      <div className="landing-hero">
        <div className="landing-sparkle">✦</div>
        <h1 className="landing-title">
          Build Wealth.<br /><span className="text-gradient">On Chain.</span>
        </h1>
        <p className="landing-sub">
          Dreamlifeway is a decentralized income platform where you earn from referrals,
          matrix levels, and royalty pools — all in USDT, all on-chain, all yours.
        </p>

        <div className="landing-cta">
          {connected && !isRegistered ? (
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/register')}>
              Complete Registration →
            </button>
          ) : (
            <>
              <ConnectButton />
              <Link to="/register" className="btn btn-outline btn-lg">Register Free</Link>
            </>
          )}
        </div>
      </div>

      <div className="how-section">
        <h2 className="section-title"><span className="text-gradient">How It Works</span></h2>
        <div className="how-grid">
          <div className="how-card">
            <div className="how-num">01</div>
            <div className="how-icon">🚀</div>
            <div className="how-title">Register & Activate</div>
            <div className="how-desc">Connect wallet, register with a referrer, and activate your first $5 package.</div>
          </div>
          <div className="how-card">
            <div className="how-num">02</div>
            <div className="how-icon">👥</div>
            <div className="how-title">Build Your Team</div>
            <div className="how-desc">Share your link. Earn 40% direct + 30% level + 30% royalty — 100% back to the community.</div>
          </div>
          <div className="how-card">
            <div className="how-num">03</div>
            <div className="how-icon">👑</div>
            <div className="how-title">Claim Royalties</div>
            <div className="how-desc">Unlock ongoing royalty pools + auto-upgrade to higher tiers.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
